import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, ClipboardCheck, ShieldCheck, HeartPulse, Activity,
  Moon, Target, FileSignature, Loader2, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GOAL_OPTIONS = [
  { value: 'fat_loss',        label: 'Zsírvesztés / Szálkásítás' },
  { value: 'muscle_gain',     label: 'Izomtömeg építés' },
  { value: 'strength',        label: 'Erőfejlesztés' },
  { value: 'posture',         label: 'Testtartás javítás' },
  { value: 'mobility',        label: 'Mobilitás / Hajlékonyság' },
  { value: 'athletic',        label: 'Sportteljesítmény' },
  { value: 'general_health',  label: 'Általános egészség' },
  { value: 'rehab',           label: 'Rehabilitáció / Sérülés utáni' },
];

const initial = {
  email: '',
  personal: {
    full_name: '', age: '', job: '', phone: '',
    height_cm: '', weight_kg: '', coaching_type: 'online',
  },
  cardio: {
    has_heart_condition: false, has_high_blood_pressure: false,
    has_diabetes: false, has_asthma: false, has_other_chronic: false,
    medications: '', allergies: '',
  },
  musculoskeletal: {
    pain_intensity: 1, pain_locations: '',
    past_injuries: '', has_surgery_history: false, surgery_details: '',
  },
  lifestyle: {
    sleep_hours: 7, work_style: 'sedentary',
    stress_level: 3, smokes: false, alcohol_per_week: 0, diet_type: 'mixed',
  },
  goals: {
    goals: [], primary_goal: '',
    training_experience: 'beginner', training_frequency_per_week: 3,
    additional_notes: '',
  },
  liability: {
    consent_truthful: false, consent_liability: false, consent_data_processing: false,
  },
};

const SectionCard = ({ icon: Icon, step, title, subtitle, children }) => (
  <section
    data-testid={`anamnesis-section-${step}`}
    className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
  >
    <div className="px-6 md:px-8 pt-6 pb-4 border-b border-gray-100 flex items-start gap-4">
      <div className="shrink-0 w-11 h-11 rounded-xl bg-yellow-500/10 border border-yellow-500/20 grid place-items-center">
        <Icon size={20} className="text-yellow-700" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-bold tracking-widest uppercase text-yellow-700">
          {step}. szekció
        </div>
        <h2
          className="text-xl md:text-2xl font-bold text-gray-900 leading-tight"
          style={{ fontFamily: 'Montserrat, sans-serif' }}
        >
          {title}
        </h2>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
    </div>
    <div className="px-6 md:px-8 py-6 space-y-5">{children}</div>
  </section>
);

const AnamnesisFormPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useDocumentMeta({
    title: 'Anamnézis és Állapotfelmérési Kérdőív | Orosz Coaching',
    description:
      'Töltsd ki az online anamnézis és állapotfelmérési kérdőívet, hogy személyre szabott, tudományos alapokon nyugvó edzéstervet építhessünk.',
    canonical: `${typeof window !== 'undefined' ? window.location.origin : ''}/onboarding/kerdoiv`,
    type: 'website',
  });

  const set = (section, key) => (eOrVal) => {
    const value = eOrVal && eOrVal.target !== undefined ? eOrVal.target.value : eOrVal;
    setForm((p) => ({ ...p, [section]: { ...p[section], [key]: value } }));
  };
  const setBool = (section, key) => (checked) =>
    setForm((p) => ({ ...p, [section]: { ...p[section], [key]: !!checked } }));

  const toggleGoal = (val) => () => {
    setForm((p) => {
      const cur = new Set(p.goals.goals);
      if (cur.has(val)) cur.delete(val); else cur.add(val);
      return { ...p, goals: { ...p.goals, goals: Array.from(cur) } };
    });
  };

  const validate = () => {
    const { email, personal, liability } = form;
    if (!email || !email.includes('@')) return 'Adj meg egy érvényes e-mail címet.';
    if (!personal.full_name.trim()) return 'A teljes név megadása kötelező.';
    if (!personal.age || Number(personal.age) < 14) return 'A korod legyen legalább 14 év.';
    if (!personal.job.trim()) return 'A foglalkozás megadása kötelező.';
    if (!personal.height_cm || Number(personal.height_cm) < 120) return 'A magasság megadása kötelező.';
    if (!personal.weight_kg || Number(personal.weight_kg) < 30) return 'A súly megadása kötelező.';
    if (!liability.consent_truthful || !liability.consent_liability || !liability.consent_data_processing) {
      return 'Mindhárom felelősségvállalási nyilatkozat elfogadása kötelező.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        personal: {
          ...form.personal,
          age: Number(form.personal.age),
          height_cm: Number(form.personal.height_cm),
          weight_kg: Number(form.personal.weight_kg),
        },
        musculoskeletal: {
          ...form.musculoskeletal,
          pain_intensity: Number(form.musculoskeletal.pain_intensity),
        },
        lifestyle: {
          ...form.lifestyle,
          sleep_hours: Number(form.lifestyle.sleep_hours),
          stress_level: Number(form.lifestyle.stress_level),
          alcohol_per_week: Number(form.lifestyle.alcohol_per_week),
        },
        goals: {
          ...form.goals,
          training_frequency_per_week: Number(form.goals.training_frequency_per_week),
        },
      };
      await axios.post(`${API}/anamnesis/submit`, payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e2) {
      const msg = e2?.response?.data?.detail || 'A kérdőív elküldése sikertelen. Kérlek, próbáld meg újra.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-28 pb-24 container mx-auto px-6 max-w-2xl text-center" data-testid="anamnesis-success">
          <div className="w-16 h-16 mx-auto rounded-full bg-yellow-500/10 grid place-items-center mb-6">
            <CheckCircle2 size={32} className="text-yellow-600" />
          </div>
          <h1
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 leading-tight"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Köszönöm a kitöltést!
          </h1>
          <p className="text-base text-gray-600 leading-relaxed mb-6">
            A kérdőíved beérkezett. 24 órán belül átnézem a válaszaidat, és felveszem
            veled a kapcsolatot az általad megadott elérhetőségen.
          </p>
          <p className="text-sm text-gray-500 mb-10">
            Egy megerősítő e-mailt is küldtem a <strong className="text-gray-900">{form.email}</strong> címre.
          </p>
          <Button
            data-testid="anamnesis-back-home"
            onClick={() => navigate('/')}
            className="bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold"
          >
            Vissza a főoldalra
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="pt-28 pb-20">
        <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
          <Link
            to="/"
            data-testid="anamnesis-back-link"
            className="inline-flex items-center text-sm text-yellow-700 hover:text-yellow-600 mb-6 transition-colors"
          >
            <ArrowLeft size={14} className="mr-1.5" />
            Vissza a főoldalra
          </Link>

          <div className="mb-10">
            <div className="flex items-center gap-3 mb-3">
              <ClipboardCheck size={22} className="text-yellow-600" />
              <span className="text-xs font-bold tracking-widest uppercase text-yellow-700">
                Új ügyfél onboarding
              </span>
            </div>
            <h1
              className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Anamnézis és Állapotfelmérési Kérdőív
            </h1>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed">
              Köszönöm, hogy csatlakoztál! Ezt a kérdőívet a biztonságos és személyre
              szabott edzéstervezés érdekében kérlek <strong>teljesen, a valóságnak
              megfelelően</strong> töltsd ki. Az itt megadott adatokat szigorúan bizalmasan kezelem.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="anamnesis-form">
            {/* E-mail (top-level) */}
            <section className="bg-white rounded-2xl border border-gray-200 shadow-sm px-6 md:px-8 py-6">
              <Label htmlFor="email" className="text-sm font-semibold text-gray-900">
                E-mail cím (a vásárláshoz használt) *
              </Label>
              <Input
                id="email"
                data-testid="anamnesis-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                className="mt-2"
                placeholder="vasarlo@example.com"
              />
            </section>

            {/* Section 1 — Personal */}
            <SectionCard icon={FileSignature} step={1} title="Személyes adatok" subtitle="Alapinformációk a profilodhoz">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="full_name">Teljes név *</Label>
                  <Input
                    id="full_name" data-testid="anamnesis-name" required
                    value={form.personal.full_name}
                    onChange={set('personal', 'full_name')}
                  />
                </div>
                <div>
                  <Label htmlFor="age">Életkor (év) *</Label>
                  <Input
                    id="age" data-testid="anamnesis-age" type="number" min="14" max="100" required
                    value={form.personal.age}
                    onChange={set('personal', 'age')}
                  />
                </div>
                <div>
                  <Label htmlFor="job">Foglalkozás *</Label>
                  <Input
                    id="job" data-testid="anamnesis-job" required
                    value={form.personal.job}
                    onChange={set('personal', 'job')}
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Telefonszám</Label>
                  <Input
                    id="phone" data-testid="anamnesis-phone" type="tel"
                    value={form.personal.phone}
                    onChange={set('personal', 'phone')}
                    placeholder="+36 ..."
                  />
                </div>
                <div>
                  <Label htmlFor="height_cm">Magasság (cm) *</Label>
                  <Input
                    id="height_cm" data-testid="anamnesis-height" type="number" min="120" max="230" required
                    value={form.personal.height_cm}
                    onChange={set('personal', 'height_cm')}
                  />
                </div>
                <div>
                  <Label htmlFor="weight_kg">Testsúly (kg) *</Label>
                  <Input
                    id="weight_kg" data-testid="anamnesis-weight" type="number" step="0.1" min="30" max="250" required
                    value={form.personal.weight_kg}
                    onChange={set('personal', 'weight_kg')}
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-semibold text-gray-900">Coaching típusa *</Label>
                <RadioGroup
                  data-testid="anamnesis-coaching-type"
                  value={form.personal.coaching_type}
                  onValueChange={(v) => set('personal', 'coaching_type')(v)}
                  className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3"
                >
                  <label className="flex items-start gap-3 border border-gray-200 rounded-xl p-4 cursor-pointer hover:border-yellow-500/60 hover:bg-yellow-50/30 transition">
                    <RadioGroupItem value="online" id="ct-online" data-testid="anamnesis-coaching-online" />
                    <div>
                      <div className="font-semibold text-gray-900">🌐 Online coaching</div>
                      <div className="text-xs text-gray-500 mt-0.5">Távoli felmérés, online program</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 border border-gray-200 rounded-xl p-4 cursor-pointer hover:border-yellow-500/60 hover:bg-yellow-50/30 transition">
                    <RadioGroupItem value="offline_miskolc" id="ct-offline" data-testid="anamnesis-coaching-offline" />
                    <div>
                      <div className="font-semibold text-gray-900">🏛️ Személyes (Miskolc)</div>
                      <div className="text-xs text-gray-500 mt-0.5">Helyszíni fizikai diagnosztika</div>
                    </div>
                  </label>
                </RadioGroup>
              </div>
            </SectionCard>

            {/* Section 2 — Cardio */}
            <SectionCard icon={HeartPulse} step={2} title="Egészség / Cardio" subtitle="Krónikus állapotok és aktuális gyógyszerek">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  ['has_heart_condition',    'Szív / keringési probléma'],
                  ['has_high_blood_pressure','Magas vérnyomás'],
                  ['has_diabetes',           'Cukorbetegség'],
                  ['has_asthma',             'Asztma / légúti betegség'],
                  ['has_other_chronic',      'Egyéb krónikus betegség'],
                ].map(([k, label]) => (
                  <label key={k} className="flex items-center gap-3 border border-gray-200 rounded-lg px-4 py-3 cursor-pointer hover:bg-gray-50">
                    <Checkbox
                      data-testid={`anamnesis-cardio-${k}`}
                      checked={form.cardio[k]}
                      onCheckedChange={setBool('cardio', k)}
                    />
                    <span className="text-sm text-gray-800">{label}</span>
                  </label>
                ))}
              </div>
              <div>
                <Label htmlFor="medications">Jelenleg szedett gyógyszerek</Label>
                <Textarea
                  id="medications" data-testid="anamnesis-medications" rows={3}
                  value={form.cardio.medications}
                  onChange={set('cardio', 'medications')}
                  placeholder="Pl. vérnyomáscsökkentő, vitamin, kiegészítő..."
                />
              </div>
              <div>
                <Label htmlFor="allergies">Ismert allergiák / ételintoleranciák</Label>
                <Textarea
                  id="allergies" data-testid="anamnesis-allergies" rows={2}
                  value={form.cardio.allergies}
                  onChange={set('cardio', 'allergies')}
                />
              </div>
            </SectionCard>

            {/* Section 3 — Musculoskeletal */}
            <SectionCard icon={Activity} step={3} title="Mozgásszervi állapot" subtitle="Sérülések, fájdalom és műtéti előzmények">
              <div>
                <Label className="text-sm font-semibold text-gray-900">Aktuális fájdalom intenzitása</Label>
                <p className="text-xs text-gray-500 mt-1 mb-3">1 = nincs fájdalom, 5 = erős, mindennapokat akadályozó</p>
                <RadioGroup
                  data-testid="anamnesis-pain-intensity"
                  value={String(form.musculoskeletal.pain_intensity)}
                  onValueChange={(v) => set('musculoskeletal', 'pain_intensity')(Number(v))}
                  className="flex gap-2 sm:gap-3"
                >
                  {[1, 2, 3, 4, 5].map((n) => (
                    <label key={n} className="flex-1 flex items-center justify-center gap-2 border border-gray-200 rounded-lg py-3 cursor-pointer hover:border-yellow-500/60">
                      <RadioGroupItem value={String(n)} id={`pi-${n}`} data-testid={`anamnesis-pain-${n}`} />
                      <span className="text-sm font-semibold text-gray-800">{n}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
              <div>
                <Label htmlFor="pain_locations">Hol / mikor jelentkezik a fájdalom?</Label>
                <Textarea
                  id="pain_locations" data-testid="anamnesis-pain-locations" rows={2}
                  value={form.musculoskeletal.pain_locations}
                  onChange={set('musculoskeletal', 'pain_locations')}
                />
              </div>
              <div>
                <Label htmlFor="past_injuries">Korábbi sérülések (csont, izom, ízület)</Label>
                <Textarea
                  id="past_injuries" data-testid="anamnesis-past-injuries" rows={3}
                  value={form.musculoskeletal.past_injuries}
                  onChange={set('musculoskeletal', 'past_injuries')}
                />
              </div>
              <div className="space-y-3">
                <label className="flex items-center gap-3 border border-gray-200 rounded-lg px-4 py-3 cursor-pointer hover:bg-gray-50">
                  <Checkbox
                    data-testid="anamnesis-has-surgery"
                    checked={form.musculoskeletal.has_surgery_history}
                    onCheckedChange={setBool('musculoskeletal', 'has_surgery_history')}
                  />
                  <span className="text-sm text-gray-800">Volt műtéti beavatkozásom (mozgásszervi)</span>
                </label>
                {form.musculoskeletal.has_surgery_history && (
                  <Textarea
                    data-testid="anamnesis-surgery-details" rows={2}
                    value={form.musculoskeletal.surgery_details}
                    onChange={set('musculoskeletal', 'surgery_details')}
                    placeholder="Mikor, mit, mennyi rehabilitáció után?"
                  />
                )}
              </div>
            </SectionCard>

            {/* Section 4 — Lifestyle */}
            <SectionCard icon={Moon} step={4} title="Életmód" subtitle="Alvás, munkavégzés és stressz-szint">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="sleep_hours">Átlagos alvás (óra/nap)</Label>
                  <Input
                    id="sleep_hours" data-testid="anamnesis-sleep" type="number" step="0.5" min="3" max="14"
                    value={form.lifestyle.sleep_hours}
                    onChange={set('lifestyle', 'sleep_hours')}
                  />
                </div>
                <div>
                  <Label>Munka jellege</Label>
                  <Select
                    value={form.lifestyle.work_style}
                    onValueChange={(v) => set('lifestyle', 'work_style')(v)}
                  >
                    <SelectTrigger data-testid="anamnesis-work-style">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sedentary">Ülő (irodai)</SelectItem>
                      <SelectItem value="mixed">Vegyes</SelectItem>
                      <SelectItem value="physical">Fizikai munka</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-sm font-semibold text-gray-900">Stressz szint</Label>
                <p className="text-xs text-gray-500 mt-1 mb-3">1 = minimális, 5 = krónikusan magas</p>
                <RadioGroup
                  data-testid="anamnesis-stress"
                  value={String(form.lifestyle.stress_level)}
                  onValueChange={(v) => set('lifestyle', 'stress_level')(Number(v))}
                  className="flex gap-2 sm:gap-3"
                >
                  {[1, 2, 3, 4, 5].map((n) => (
                    <label key={n} className="flex-1 flex items-center justify-center gap-2 border border-gray-200 rounded-lg py-3 cursor-pointer hover:border-yellow-500/60">
                      <RadioGroupItem value={String(n)} id={`st-${n}`} data-testid={`anamnesis-stress-${n}`} />
                      <span className="text-sm font-semibold text-gray-800">{n}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <label className="flex items-center gap-3 border border-gray-200 rounded-lg px-4 py-3 cursor-pointer hover:bg-gray-50">
                  <Checkbox
                    data-testid="anamnesis-smokes"
                    checked={form.lifestyle.smokes}
                    onCheckedChange={setBool('lifestyle', 'smokes')}
                  />
                  <span className="text-sm text-gray-800">Dohányzom</span>
                </label>
                <div>
                  <Label htmlFor="alcohol">Alkohol egység / hét</Label>
                  <Input
                    id="alcohol" data-testid="anamnesis-alcohol" type="number" min="0" max="50"
                    value={form.lifestyle.alcohol_per_week}
                    onChange={set('lifestyle', 'alcohol_per_week')}
                  />
                </div>
                <div>
                  <Label>Étrend típusa</Label>
                  <Select
                    value={form.lifestyle.diet_type}
                    onValueChange={(v) => set('lifestyle', 'diet_type')(v)}
                  >
                    <SelectTrigger data-testid="anamnesis-diet">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mixed">Vegyes</SelectItem>
                      <SelectItem value="vegetarian">Vegetáriánus</SelectItem>
                      <SelectItem value="vegan">Vegán</SelectItem>
                      <SelectItem value="keto">Keto / LCHF</SelectItem>
                      <SelectItem value="other">Egyéb</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </SectionCard>

            {/* Section 5 — Goals */}
            <SectionCard icon={Target} step={5} title="Edzési célok" subtitle="Mit szeretnél elérni a közös munka során?">
              <div>
                <Label className="text-sm font-semibold text-gray-900 mb-3 block">Válaszd ki az összes releváns célt (több is lehet)</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {GOAL_OPTIONS.map((g) => {
                    const checked = form.goals.goals.includes(g.value);
                    return (
                      <label
                        key={g.value}
                        className={`flex items-center gap-3 border rounded-lg px-4 py-3 cursor-pointer transition ${
                          checked
                            ? 'border-yellow-500 bg-yellow-50/60'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <Checkbox
                          data-testid={`anamnesis-goal-${g.value}`}
                          checked={checked}
                          onCheckedChange={toggleGoal(g.value)}
                        />
                        <span className="text-sm text-gray-800">{g.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label htmlFor="primary_goal">Mi az elsődleges, legfontosabb célod?</Label>
                <Textarea
                  id="primary_goal" data-testid="anamnesis-primary-goal" rows={2}
                  value={form.goals.primary_goal}
                  onChange={set('goals', 'primary_goal')}
                  placeholder="Pl. 8 kg fogyás 6 hónap alatt; első erőemelő verseny..."
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label>Edzéstapasztalat</Label>
                  <Select
                    value={form.goals.training_experience}
                    onValueChange={(v) => set('goals', 'training_experience')(v)}
                  >
                    <SelectTrigger data-testid="anamnesis-experience">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Kezdő (&lt; 1 év)</SelectItem>
                      <SelectItem value="intermediate">Haladó (1–3 év)</SelectItem>
                      <SelectItem value="advanced">Tapasztalt (3+ év)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="training_freq">Heti edzések száma (terv)</Label>
                  <Input
                    id="training_freq" data-testid="anamnesis-frequency"
                    type="number" min="0" max="14"
                    value={form.goals.training_frequency_per_week}
                    onChange={set('goals', 'training_frequency_per_week')}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="additional_notes">Egyéb megjegyzések (preferenciák, korlátok)</Label>
                <Textarea
                  id="additional_notes" data-testid="anamnesis-notes" rows={3}
                  value={form.goals.additional_notes}
                  onChange={set('goals', 'additional_notes')}
                />
              </div>
            </SectionCard>

            {/* Section 6 — Liability */}
            <SectionCard icon={ShieldCheck} step={6} title="Felelősségvállalás" subtitle="A kérdőív elküldéséhez mindhárom nyilatkozat elfogadása kötelező">
              <label className="flex items-start gap-3 border border-gray-200 rounded-lg px-4 py-3 cursor-pointer hover:bg-gray-50">
                <Checkbox
                  data-testid="anamnesis-consent-truthful"
                  checked={form.liability.consent_truthful}
                  onCheckedChange={setBool('liability', 'consent_truthful')}
                  className="mt-0.5"
                />
                <span className="text-sm text-gray-800 leading-relaxed">
                  Kijelentem, hogy a kérdőívben megadott adatok a valóságnak megfelelőek, és a legjobb tudásom szerint, hiánytalanul töltöttem ki.
                </span>
              </label>
              <label className="flex items-start gap-3 border border-gray-200 rounded-lg px-4 py-3 cursor-pointer hover:bg-gray-50">
                <Checkbox
                  data-testid="anamnesis-consent-liability"
                  checked={form.liability.consent_liability}
                  onCheckedChange={setBool('liability', 'consent_liability')}
                  className="mt-0.5"
                />
                <span className="text-sm text-gray-800 leading-relaxed">
                  Tudomásul veszem, hogy az edzések kockázattal járhatnak, és kijelentem, hogy az orvosi/egészségügyi állapotom alapján alkalmas vagyok a fizikai aktivitásra. Saját felelősségemre veszek részt a programban.
                </span>
              </label>
              <label className="flex items-start gap-3 border border-gray-200 rounded-lg px-4 py-3 cursor-pointer hover:bg-gray-50">
                <Checkbox
                  data-testid="anamnesis-consent-data"
                  checked={form.liability.consent_data_processing}
                  onCheckedChange={setBool('liability', 'consent_data_processing')}
                  className="mt-0.5"
                />
                <span className="text-sm text-gray-800 leading-relaxed">
                  Hozzájárulok a megadott egészségügyi és személyes adataim bizalmas kezeléséhez az{' '}
                  <Link to="/adatvedelem" className="text-yellow-700 hover:text-yellow-600 font-semibold underline">
                    Adatkezelési tájékoztató
                  </Link>{' '}
                  szerint.
                </span>
              </label>
            </SectionCard>

            <div className="pt-2">
              <Button
                type="submit"
                data-testid="anamnesis-submit"
                disabled={submitting}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-gray-900 font-bold h-14 text-base rounded-xl shadow-lg shadow-yellow-500/20"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={18} />
                    Küldés folyamatban...
                  </>
                ) : (
                  <>Kérdőív elküldése</>
                )}
              </Button>
              <p className="text-center text-xs text-gray-500 mt-3">
                A küldés után e-mail megerősítést kapsz, és 24 órán belül kapcsolatba lépek veled.
              </p>
            </div>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AnamnesisFormPage;
